import React from 'react';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import { useSubscription } from '../../context/subscription-context';

export const TrialBanner: React.FC = () => {
  const {
    info,
    daysRemaining,
    upgrade,
    startTrialIfEligible,
    dismissBanner,
    bannerDismissed,
    loading,
  } = useSubscription();

  // Kick off trial if none when banner mounts (lazy activation pattern)
  React.useEffect(() => {
    if (!loading && info?.status === 'none') {
      void startTrialIfEligible();
    }
  }, [info?.status, loading, startTrialIfEligible]);

  if (bannerDismissed) return null;

  // Show only in trialing or trial_expired
  const status = info?.status;
  if (status !== 'trialing' && status !== 'trial_expired') return null;

  const expired =
    status === 'trial_expired' ||
    (daysRemaining !== undefined && daysRemaining <= 0);

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Alert
        severity={expired ? 'error' : 'info'}
        onClose={dismissBanner}
        icon={false}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            flexWrap: 'wrap',
            gap: 1,
          }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {expired
              ? 'Your trial has ended. Upgrade to continue full access.'
              : `Trial: ${daysRemaining ?? '–'} day${daysRemaining === 1 ? '' : 's'} remaining`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant={expired ? 'contained' : 'outlined'}
              color={expired ? 'primary' : 'secondary'}
              onClick={() => upgrade()}>
              {expired ? 'Upgrade Now' : 'Upgrade'}
            </Button>
            {!expired && (
              <Button size="small" color="inherit" onClick={dismissBanner}>
                Dismiss
              </Button>
            )}
          </Box>
        </Box>
        {!expired && daysRemaining !== undefined && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (daysRemaining / 14) * 100)}
            sx={{ height: 6, borderRadius: 3 }}
          />
        )}
      </Alert>
    </Box>
  );
};
